import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

interface TransactionStatus {
  transactionId: string;
  status: string;
  statusDescription: string;
  amount: number;
  currency: string;
  errorMessage?: string;
  createdAt: string;
  lastModifiedAt?: string;
}

export const PaymentCancel: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [transactionStatus, setTransactionStatus] = useState<TransactionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const transactionId = searchParams.get('transactionId');

  useEffect(() => {
    const checkTransactionStatus = async () => {
      if (!transactionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/payment/status/${transactionId}`);
        setTransactionStatus(response.data);
      } catch (err: unknown) {
        console.error('Error checking transaction status:', err);
      } finally {
        setLoading(false);
      }
    };

    void checkTransactionStatus();
  }, [transactionId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stitch-canvas px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
          <p className="mt-4 text-lg text-stitch-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-stitch-canvas px-4 py-12 pt-20 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg">
        <div className="relative overflow-hidden rounded-stitch-xl border border-stitch-border bg-stitch-surface p-8 text-center shadow-stitch-modal md:p-12">
          <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-stitch-warning to-stitch-accent" />
          <div className="mb-8 flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-4 border-stitch-warning-soft bg-stitch-warning-soft">
              <span
                className="material-symbols-outlined text-5xl text-stitch-warning"
                style={{ fontVariationSettings: "'wght' 600" }}
              >
                warning
              </span>
            </div>
          </div>
          <h1 className="mb-4 text-3xl font-bold tracking-tight text-stitch-fg">Payment Cancelled</h1>
          <p className="mx-auto mb-10 max-w-sm leading-relaxed text-stitch-muted">
            Your payment was not completed. No charges were made to your account. This could be due to a timeout or the
            transaction being declined.
          </p>

          {transactionStatus && (
            <div className="mb-8 rounded-stitch-lg bg-stitch-surface-2 p-4 text-left">
              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="font-medium text-stitch-muted">Transaction ID:</dt>
                  <dd className="text-stitch-fg">{transactionStatus.transactionId}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="font-medium text-stitch-muted">Status:</dt>
                  <dd className="text-stitch-fg">{transactionStatus.statusDescription}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="font-medium text-stitch-muted">Amount:</dt>
                  <dd className="text-stitch-fg">
                    {transactionStatus.currency} {transactionStatus.amount.toFixed(2)}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Link
              to="/interviewers"
              className="w-full rounded-stitch-lg bg-stitch-primary px-6 py-3.5 font-semibold text-white shadow-md shadow-stitch-primary/20 transition-all hover:bg-stitch-primary-hover active:scale-[0.98]"
            >
              Retry Payment
            </Link>
            <Link
              to="/"
              className="w-full rounded-stitch-lg border border-stitch-border bg-stitch-surface px-6 py-3.5 font-semibold text-stitch-fg transition-all hover:bg-stitch-surface-2 active:scale-[0.98]"
            >
              Back to Home
            </Link>
            <Link
              to="/bookings"
              className="w-full rounded-stitch-lg border border-stitch-border px-6 py-3.5 text-sm font-medium text-stitch-muted hover:bg-stitch-surface-2"
            >
              View My Bookings
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};
